import React from 'react';
import { Column } from '@tanstack/react-table';
import { ColumnsVisibility } from '@teetsh/app/src/containers/Eleves/List/Table/Header/ColumnsVisibility';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { UserPlusIcon as UserAdd } from '@heroicons/react/20/solid';
import { Settings } from '@teetsh/app/src/containers/Eleves/List/Table/Settings';
import { SmallNumber } from '@teetsh/app/src/containers/Eleves/List/Table/Header/components/SmallNumber';
import { CustomFieldDefinition } from '@teetsh/app/src/types/customField';
import Popover from '@teetsh/app/src/components/Popover';
import {
  ChevronDownIcon as ChevronDown,
  FunnelIcon as Funnel,
  ViewColumnsIcon as ViewColumns,
} from '@heroicons/react/24/outline';
import { FunnelIcon as FunnelSolid } from '@heroicons/react/24/solid';
import tw from 'twin.macro';
import { useIsBelowBreakpoint } from '@teetsh/app/src/components/ResponsiveSwitcher/useIsBelowSize';
import { useTranslation } from 'react-i18next';
import { useOpenAddEleveModal } from 'containers/Eleves/AddEleveDialog/atoms';
import {
  useIsOpenFilterBar,
  useToggleFilterBar,
} from 'containers/Eleves/List/Table/FilterBar/atom';
import { SaveStatus } from 'containers/Eleves/List/Table/Header/SaveStatus';

interface Props {
  columns: Column<any, any>[];
  onColumnOrderChange: (v: string[]) => void;
  nbOfFilters: number;
  viewId: string;
  print?: boolean;
  customFields: CustomFieldDefinition[];
}

export function Header(props: Props) {
  const { columns, onColumnOrderChange, nbOfFilters, viewId, print } = props;
  const isMobile = useIsBelowBreakpoint('md');
  const { t } = useTranslation(['common', 'eleve']);
  const openAddEleveModal = useOpenAddEleveModal();
  const visibleColumns = columns.filter(c => c.getIsVisible());
  const isOpenFilterBar = useIsOpenFilterBar();
  const toggleFilterBar = useToggleFilterBar();
  return (
    <div tw="flex items-center justify-between">
      <div />

      {print ? (
        <div />
      ) : (
        <div tw="w-full flex items-center flex-wrap sm:flex-nowrap gap-y-2.5 sm:gap-x-2 mt-0.5 sm:mt-0 sm:mb-2">
          <div tw="px-2 grow flex [&_svg]:text-muted">
            <SaveStatus />
          </div>
          <div>
            <Button
              icon={nbOfFilters > 0 ? FunnelSolid : Funnel}
              intent={isOpenFilterBar ? 'primary' : 'none'}
              small
              minimal
              onClick={() => toggleFilterBar()}
              // those are needed to keep consistent spacing between elements and button size,
              // regardless of the number of filters, or screensize
              iconStyle={tw`mx-0`}
              tw="gap-1.5 px-2"
            >
              {!isMobile && t('common:filterBar.title')}
              {nbOfFilters > 0 && (
                <SmallNumber tw="ml-0">{nbOfFilters}</SmallNumber>
              )}
            </Button>
          </div>
          <Popover as="div">
            <div>
              <Popover.Button
                icon={ViewColumns}
                rightIcon={ChevronDown}
                className="group"
                small
                minimal
                aria-label="Bouton Colonnes"
                iconStyle={tw`mx-0`}
                // those are needed to keep consistent spacing between elements and button size,
                // especially on mobile, where text is not displayed
                rightIconStyle={tw`mx-0`}
                tw="gap-1.5 px-2"
              >
                {!isMobile && t('common:columns')}
                <SmallNumber tw="ml-0">
                  {visibleColumns.length}/{columns.length}
                </SmallNumber>
              </Popover.Button>
            </div>

            <Popover.Panel overflow="visible" size={isMobile ? 'sm' : 'xl'}>
              <div css={[tw`py-4`]}>
                <ColumnsVisibility
                  customFields={props.customFields}
                  viewId={viewId}
                  columns={columns}
                  onColumnOrderChange={onColumnOrderChange}
                />
              </div>
            </Popover.Panel>
          </Popover>
          <Settings loading={false} mobile={false} />
          <Button
            small
            onClick={() => openAddEleveModal({ viewId })}
            intent="primary"
            icon={UserAdd}
            tw="whitespace-nowrap w-full sm:w-auto"
          >
            {t('eleve:actions.addEleves')}
          </Button>
        </div>
      )}
    </div>
  );
}
